import React, { useState, useEffect } from 'react';
import { FaTimes, FaArrowDown } from 'react-icons/fa';
import { branding } from '../constants/branding';
import { apiService } from '../services/apiService';
import { formatAmount } from '../utils/currency';

interface TradingAccountWithdrawModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  accounts: any[];
  tradingWallet: any;
  userId: number;
}

const TradingAccountWithdrawModal: React.FC<TradingAccountWithdrawModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  accounts,
  tradingWallet,
  userId
}) => {
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setSelectedAccountId('');
      setAmount('');
      setReason('');
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedAccountId) {
      setError('Please select a destination account');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const availableBalance = tradingWallet?.availableBalance || 0;
    if (parseFloat(amount) > availableBalance) {
      setError(`Insufficient balance. Available: ${formatAmount(availableBalance, tradingWallet?.currency || 'USD')}`);
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.post(`/api/retail/trading/wallets/${userId}/withdraw`, {
        amount: parseFloat(amount),
        reason: reason || `Withdrawal to account ${selectedAccountId}`
      });

      if (response.success) {
        onSuccess();
        setAmount('');
        setSelectedAccountId('');
        setReason('');
        onClose();
      } else {
        setError(response.error || 'Withdrawal failed');
      }
    } catch (err: any) {
      setError(err.message || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedAccount = accounts.find(acc => acc.id.toString() === selectedAccountId);
  const availableBalance = tradingWallet?.availableBalance || 0;

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: branding.textColor }}>
            Withdraw from Trading Account
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 20,
              color: '#666',
              cursor: 'pointer',
              padding: 8,
              borderRadius: 4
            }}
          >
            <FaTimes />
          </button>
        </div>

        {tradingWallet && (
          <div style={{
            background: '#fef2f2',
            padding: 16,
            borderRadius: 8,
            marginBottom: 20,
            border: '2px solid #ef4444'
          }}>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Available Balance</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444' }}>
              {formatAmount(availableBalance, tradingWallet.currency || 'USD')}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: branding.textColor }}>
              To Account
            </label>
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              style={{
                width: '100%',
                padding: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 14
              }}
              required
            >
              <option value="">Select an account</option>
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.type} - {formatAmount(account.balance, account.currency)}
                </option>
              ))}
            </select>
          </div>

          {selectedAccount && (
            <div style={{
              background: '#f9fafb',
              padding: 12,
              borderRadius: 8,
              marginBottom: 20,
              fontSize: 13,
              color: '#666'
            }}>
              Account Balance: <strong>{formatAmount(selectedAccount.balance, selectedAccount.currency)}</strong>
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: branding.textColor }}>
              Amount
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0.01"
              max={availableBalance}
              style={{
                width: '100%',
                padding: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 14
              }}
              required
            />
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Maximum: {formatAmount(availableBalance, tradingWallet?.currency || 'USD')}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: branding.textColor }}>
              Reason (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Add a note about this withdrawal..."
              rows={3}
              style={{
                width: '100%',
                padding: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 14,
                resize: 'vertical'
              }}
            />
          </div>

          {error && (
            <div style={{
              background: '#fee2e2',
              color: '#991b1b',
              padding: 12,
              borderRadius: 8,
              marginBottom: 20,
              fontSize: 14
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                background: 'white',
                color: branding.textColor,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: 12,
                border: 'none',
                borderRadius: 8,
                background: loading ? '#9ca3af' : branding.primaryColor,
                color: '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              {loading ? 'Processing...' : 'Withdraw'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
  padding: 20
};

const modalContentStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: 16,
  padding: 32,
  maxWidth: 500,
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto'
};

const modalHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 24,
  paddingBottom: 16,
  borderBottom: `2px solid ${branding.primaryColor}`
};

export default TradingAccountWithdrawModal;

